import {
  Breadcrumbs,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { FormField } from "@/components/core/form-field";
import { CardLoadingSkeleton, LoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link } from "@/components/core/tanstack-link";
import { DownloadInvoice } from "@/components/invoices/download-invoice";
import { NotFound } from "@/components/not-found";
import { formatCurrency, formatDate } from "@/helpers/intl";
import {
  InvoiceStatus,
  portalInvoiceDownload,
  usePortal,
  usePortalPlanInstallment,
} from "@/models/backend";

export const Route = createFileRoute("/_portal/my-invoices/pp/$installmentId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { installmentId } = Route.useParams();
  const { token } = Route.useSearch();
  const { data: customer, isError, error } = usePortal({ token });
  const { data: installment, isLoading } = usePortalPlanInstallment(installmentId, { token });
  if (isError && "status" in error && error.status === 404) {
    return <NotFound />;
  }

  if (isLoading) {
    return <CardLoadingSkeleton />;
  }
  const last_invoice = (installment?.invoices.length ?? 1) - 1;
  return (
    <Stack spacing={4}>
      <Breadcrumbs separator=">" aria-label="breadcrumb">
        <Link to="/my-invoices" search={{ token }}>
          All invoices
        </Link>
        <span>
          {installment?.invoices.map((inv, idx) => (
            <span key={inv.id}>
              {idx !== 0 && (idx === last_invoice ? " & " : ", ")}
              <Link to="/my-invoices/$invoiceId" search={{ token }} params={{ invoiceId: inv.id }}>
                {inv.number}
              </Link>
            </span>
          ))}
        </span>
        <Typography>
          {installment?.nth} of {installment?.total}
        </Typography>
      </Breadcrumbs>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Typography variant="h2">{installment?.number}</Typography>
        {installment?.status === InvoiceStatus.overdue ? (
          <Chip sx={{ ml: "auto" }} color="warning" variant="outlined" label="Payment overdue" />
        ) : installment?.status === InvoiceStatus.posted ? (
          <Chip
            sx={{ ml: "auto" }}
            color="secondary"
            variant="outlined"
            label="Payment outstanding"
          />
        ) : (
          <Chip
            sx={{ ml: "auto" }}
            color="primary"
            variant="outlined"
            label="Payments up to date"
          />
        )}
      </Stack>
      <Stack direction="row" spacing={2}>
        {!installment ? (
          <LoadingSkeleton />
        ) : (
          installment.invoices.map((invoice) => (
            <DownloadInvoice
              key={invoice.id}
              invoice={invoice}
              download={() =>
                portalInvoiceDownload(invoice.id, { token }, { responseType: "blob" })
              }
              label={`Download invoice ${invoice.number}`}
            />
          ))
        )}
      </Stack>
      <Stack spacing={2}>
        <Typography variant="h4">Summary</Typography>
        <Grid container sx={{ pl: "16px" }}>
          <Grid size={6}>
            <Stack spacing={3}>
              <FormField label="Billed to" component={Typography}>
                {customer?.name}
              </FormField>
              <FormField
                label={`Invoice number${last_invoice > 0 ? "s" : ""}`}
                component={Typography}
              >
                {installment?.invoices.map((inv, idx) => (
                  <span key={inv.id}>
                    {idx !== 0 && (idx === last_invoice ? " & " : ", ")}
                    {inv.number}
                  </span>
                ))}
              </FormField>
              <FormField label="Due date" component={Typography}>
                {formatDate(installment?.due)}
              </FormField>
            </Stack>
          </Grid>
          <Grid size={6}>
            <FormField label="From" component={Typography}>
              {customer?.organization.name}
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      <Stack spacing={2}>
        <Typography variant="h4">Items</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                Installment {installment?.nth} of {installment?.total} for{" "}
                {installment?.invoices.map((inv, idx) => (
                  <span key={inv.id}>
                    {idx !== 0 && (idx === last_invoice ? " & " : ", ")}
                    {inv.number}
                  </span>
                ))}
              </TableCell>
              <TableCell>
                {formatCurrency(installment?.total_amount, installment?.currency)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Total amount</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {formatCurrency(installment?.total_amount, installment?.currency)}
              </TableCell>
            </TableRow>
            {installment && installment?.amount_due !== installment?.total_amount && (
              <>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Amount paid</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {formatCurrency(-installment.amount_paid, installment.currency)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Balance due</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {formatCurrency(installment.amount_due, installment.currency)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Stack>
    </Stack>
  );
}
